/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, HostListener, Inject, OnInit, ViewChild, DOCUMENT} from '@angular/core';
import {FooterComponent, HamburgerComponent, HeaderComponent, LeftComponent, RightComponent} from '../templates';
import {AngularSplitModule, ISplitDirection, SplitComponent} from 'angular-split';

import {ActivatedRoute} from '@angular/router';
import {PlanModelService} from '../common/service/plan-model.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {TraitsService} from '../common/service/traits.service';
import {NlrPlanDetailService} from './plan-detail.service';
import {NAV_MENUS, PDF_CREATION_METHOD, PLAN_ACTION, NLR_TABS} from '../common/constants';
import {RuleResults} from './plan-detail.model';
import {concatMap, map, Observable, of, tap} from 'rxjs';
import {FeaturesRepositoryService} from '../common/service/features-repository.service';
import {SpatialUtilService} from '../design/services/spatial-util.service';
import {MapToolsService} from '../design/services/map-tools.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {switchMap} from 'rxjs/operators';
import {ConfirmationModalService} from '../common/component/modal';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationService} from '../common/service/navigation.service';
import {HamburgerService} from '../templates/hamburger/hamburger.service';

@Component({
    selector: 'nlr-plan-detail',
    imports: [HeaderComponent, HamburgerComponent, FooterComponent, LeftComponent, RightComponent, AngularSplitModule],
    templateUrl: './plan-detail.component.html',
    styleUrl: './plan-detail.component.scss',
    providers: [SplitComponent]
})
export class PlanDetailComponent implements OnInit {
    @ViewChild(RightComponent)
    rightComponent: RightComponent;

    title = NAV_MENUS.PLAN_DETAIL;

    protected readonly PLAN_ACTION = PLAN_ACTION;
    direction: ISplitDirection = 'horizontal';
    leftSizeGap = [0, 28, 50];
    rightSizeGap = [100, 72, 50];
    sizeIndex = 1;
    action: string;
    planLodgementId: string;
    validationResults: RuleResults[];
    isModalOpen = false;

    private readonly nonSpatialTabs = ['additionals', 'analysis', 'digital_plan', 'information', 'interests', 'certification', 'lodge'];
    private readonly nonSpatialSaveTabs = ['analysis', 'digital_plan', 'information', 'interests', 'certification', 'lodge'];
    private readonly nonSpatialValidateTabs = ['analysis', 'information', 'interests', 'certification', 'lodge'];

    leftPanelSize = () => {
        switch (this.sizeIndex) {
            case 0:
            case 2:
                return this.leftSizeGap[this.sizeIndex];
            default:
                return Math.max(28, Math.round(100 * (526 / this.document.body.offsetWidth)));
        }
    };
    rightPanelSize = () => 'auto';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private eventBus: NgEventBus,
        private route: ActivatedRoute,
        private confirmationModalService: ConfirmationModalService,
        private destroyRef: DestroyRef,
        private featuresRepositoryService: FeaturesRepositoryService,
        private mapToolsService: MapToolsService,
        private nlrPlanDetailService: NlrPlanDetailService,
        private planLodgementService: PlanLodgementService,
        private planModelService: PlanModelService,
        private traitService: TraitsService,
        private spatialUtilService: SpatialUtilService,
        private navigationService: NavigationService,
        private hamburgerService: HamburgerService
    ) {}

    ngOnInit(): void {
        this.nlrPlanDetailService.resetValidationResults();
        this.hamburgerService.hideHamburger();
        this.route.params
            .pipe(
                map(params => {
                    this.action = params['action'];
                    this.planLodgementId = params['planLodgementId'];
                })
            )
            .subscribe(() => {
                this.initialisePlanLodgement();
            });
        this.eventBus
            .on('nlr:collapsePanel')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => this.collapsePanel(meta.data));

        this.eventBus
            .on('modal::open')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.isModalOpen = true;
            });

        this.eventBus
            .on('modal::closed')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.isModalOpen = false;
            });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload($event: any) {
        if (!this.isDirty() || !this.planLodgementService.hasMinimumPlanDetails()) {
            return;
        }

        // display leave site warning
        $event.preventDefault();
    }

    isDirty(): boolean {
        return !this.planModelService.readonly && (this.planModelService.modelIsDirty() || this.spatialUtilService.hasEdits());
    }

    // Handle print
    @HostListener('document:keydown.control.p', ['$event'])
    @HostListener('document:keydown.meta.p', ['$event'])
    // Handle save
    @HostListener('document:keydown.control.s', ['$event'])
    @HostListener('document:keydown.meta.s', ['$event'])
    // Handle validate all
    @HostListener('document:keydown.shift.v', ['$event'])
    // Handle validate tab
    @HostListener('document:keydown.v', ['$event'])
    onDocumentKeyDown(e: KeyboardEvent) {
        if (e.repeat || document.querySelector('div.ng-busy') || this.isModalOpen) {
            if (
                (e.code === 'KeyP' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) ||
                (e.code === 'KeyS' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) ||
                (e.code === 'KeyV' && e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey)
            ) {
                e.preventDefault();
            }
            return;
        }

        const isNonSpatialTab = this.nonSpatialTabs.includes(this.nlrPlanDetailService.selectedTab);
        const isNonSpatialSaveTab = this.nonSpatialSaveTabs.includes(this.nlrPlanDetailService.selectedTab);
        const isNonSpatialValidateTab = this.nonSpatialValidateTabs.includes(this.nlrPlanDetailService.selectedTab);

        // Shortcuts that ARE compatible with focused input, select and textarea fields

        if (isNonSpatialTab && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
            // Ctrl+P - Print
            if (e.code === 'KeyP') {
                e.preventDefault();
                this.mapToolsService.activate('PRINT_PLAN_WINDOW').subscribe();
                return;
            }

            // Ctrl+S - Save
            if (e.code === 'KeyS') {
                // Note - The default browser event should be prevented for all non-spatial tabs, not just the non-spatial tabs that can save
                e.preventDefault();
                if (isNonSpatialSaveTab && !this.planModelService.readonly) {
                    this.eventBus.cast(`SAVE_${this.nlrPlanDetailService.selectedTab.toUpperCase()}`);
                }
                return;
            }
        }

        // Shortcuts that are NOT compatible with focused input, select and textarea fields

        const activeElement = document.activeElement && ['INPUT', 'SELECT', 'TEXTAREA'].includes(document.activeElement.tagName);

        if (activeElement || ['Alt', 'Control', 'Meta', 'Shift'].includes(e.key)) {
            return;
        }

        // Shift+V - Validate all
        if (e.code === 'KeyV' && e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            this.confirmSaveAndValidateAll();
            return;
        }

        // V - Validate
        if (isNonSpatialValidateTab && e.code === 'KeyV' && isNonSpatialTab && !e.altKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            this.eventBus.cast(`VALIDATE_${this.nlrPlanDetailService.selectedTab.toUpperCase()}`, false);
        }
    }

    canDeactivate(): Observable<boolean> {
        return this.navigationService.canDeactivate();
    }

    getLeftPanelMinSize() {
        return Math.min(100, Math.round(100 * (526 / this.document.body.offsetWidth)));
    }

    getPlanInformationForm() {
        return this.rightComponent?.planInformationComponent?.planInformationForm;
    }

    getAnalysisForm() {
        return this.rightComponent?.analysisComponent?.analysisForm;
    }

    getCertificationForm() {
        return this.rightComponent?.certificationComponent?.certificationForm;
    }

    getInterestsForm() {
        return this.rightComponent?.interestsComponent?.interestsForm;
    }

    getLodgePlanForm() {
        return this.rightComponent?.lodgePlanComponent?.lodgePlanForm;
    }

    getDigitalPlanValidationState() {
        return this.rightComponent?.digitalPlanComponent?.validationState;
    }

    collapsePanel(collapseDirection: 'left' | 'right' | 'show') {
        switch (collapseDirection) {
            case 'left':
                this.sizeIndex -= 1;
                break;
            case 'right':
                this.sizeIndex += 1;
                break;
            case 'show':
                this.sizeIndex = 1;
                break;
        }
        setTimeout(() => {
            this.eventBus.cast('zoomPlanExtent');
        }, 500);
    }

    initialisePlanLodgement() {
        if (this.planLodgementId) {
            if (
                this.action === this.PLAN_ACTION.VIEW ||
                this.action === this.PLAN_ACTION.EDIT ||
                this.action === this.PLAN_ACTION.LODGE_ADDITIONALS
            ) {
                this.planModelService.readonly = this.action === this.PLAN_ACTION.VIEW || this.action === this.PLAN_ACTION.LODGE_ADDITIONALS;

                // Opening a different plan empties the model first, so that the tabs cannot read the plan opened before this
                // one while the load is in flight: the service holds one plan at a time, and every tab reads whichever is
                // current. The same plan is left alone, because the dashboard puts an amendment or replacement in the model
                // itself before opening it as an edit.
                if (this.planModelService.getModel().id !== Number(this.planLodgementId)) {
                    this.planModelService.initPlanModel();
                    // The empty plan is its own pristine copy, so nothing is reported as unsaved in the meantime.
                    this.planModelService.updatePristineModel();
                }

                const subs = this.planLodgementService
                    .loadPlan(this.planLodgementId)
                    .pipe(
                        // Clear the previous plan's features before the traits change, so nothing reacting to it acts on them
                        tap(() => this.featuresRepositoryService.init()),
                        concatMap(() => this.calcTraits()),
                        concatMap(() => this.spatialUtilService.init()),
                        concatMap(() => {
                            this.setupDesignFramework();
                            const modelGuid = this.planModelService.getModel().guid;
                            if (modelGuid) {
                                return this.nlrPlanDetailService.validationLoad(modelGuid);
                            }
                            return of(null);
                        })
                    )
                    .subscribe({
                        next: data => {
                            if (data == null) {
                                console.debug(`${this.title} - validation error:`);
                                return;
                            }
                            console.debug(`${this.title} - validation complete`);
                            this.validationResults = data;
                            if (this.action === this.PLAN_ACTION.LODGE_ADDITIONALS) {
                                this.eventBus.cast('nlr:selectTab', NLR_TABS.ADDITIONALS);
                            }
                        },
                        error: error => {
                            console.debug(`${this.title} - validation error:`, error);
                        }
                    });
                this.eventBus.cast('nlr:busy', subs);
            } else if (this.action === this.PLAN_ACTION.AMEND) {
                //TO-DO: Need to implement with subscribe when model service returns observable
                //this.planLodgementSerivce.createNewPlanVersion(this.planLodgementId, 'amendment');
                this.calcTraits().subscribe({
                    next: () => {
                        this.setupDesignFramework();
                    },
                    error: error => {
                        console.debug(`${this.title} - retrieving plan traits error:`, error);
                    }
                });
            } else if (this.action === this.PLAN_ACTION.REPLACE) {
                //TO-DO: Need to implement with subscribe when model service returns observable
                //this.planLodgementSerivce.createNewPlanVersion(this.planLodgementId, 'replacement');
                this.calcTraits().subscribe(() => {
                    this.setupDesignFramework();
                });
            }
        } else {
            if (this.action === this.PLAN_ACTION.NEW) {
                this.planModelService.readonly = false;
                this.planModelService.validated = false;
                this.planModelService.initPlanModel();
                this.featuresRepositoryService.init();
                this.nlrPlanDetailService.resetValidationResults();
                this.spatialUtilService.init().subscribe(() => {
                    const modelGuid = this.planModelService.getModel().guid;
                    if (modelGuid) {
                        return this.nlrPlanDetailService.validationLoad(modelGuid);
                    }
                    return of(null);
                });
            }
        }
    }

    getPlanLodgementModel() {
        return this.planModelService.getModel();
    }

    calcTraits() {
        // TO-DO: Needs to be removed once backed is tested (test purpose)
        // this.eventBus.on('nlr:trait-service:traitsChanged').subscribe((meta: MetaData) => {
        //     console.log(meta.data);
        // });

        return this.traitService.setPlanTraitsRequestData({
            planPurposeCode: this.getPlanLodgementModel().planPurpose && this.getPlanLodgementModel().planPurpose.id,
            surveyMethod: this.getPlanLodgementModel().surveyMethod && this.getPlanLodgementModel().surveyMethod.planSurveyMethod
        });
    }

    setupDesignFramework() {
        console.log('setupDesignFramework');
        this.eventBus.cast('setupDesignFramework');
        this.planModelService.isNotLandgateSigned = this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.CAD_SOFTWARE;
    }

    private prepareValidateAll() {
        if (this.planModelService.readonly) {
            return of(null);
        }

        this.planLodgementService.increaseValidationAttempt();
        this.planModelService.sanitizeVariationApprovals();
        return this.planModelService.save();
    }

    private validateAll() {
        let observable: Observable<any>;

        if (this.planModelService.modelIsDirty() || this.spatialUtilService.hasEdits()) {
            observable = this.planModelService.save().pipe(
                switchMap(() => {
                    if (this.planModelService.getModel().pdfCreationMethod === 'SURVEYOR_PORTAL') {
                        return this.planLodgementService.save().pipe(
                            switchMap(() => {
                                if (this.spatialUtilService.hasEdits()) {
                                    return this.spatialUtilService.save();
                                }
                                return of(null);
                            })
                        );
                    }
                    return of(null);
                })
            );
        } else {
            observable = of(null);
        }

        const subscription = observable
            .pipe(
                concatMap(() => this.prepareValidateAll()),
                switchMap(() => {
                    // skip saving the plan model on validating each tab (done in prepareValidateAll)
                    this.nonSpatialValidateTabs.map(t => `VALIDATE_${t.toUpperCase()}`).forEach(eventName => this.eventBus.cast(eventName, true));
                    if (this.planModelService.getModel().pdfCreationMethod === 'SURVEYOR_PORTAL') {
                        return this.mapToolsService.activate('VALIDATE');
                    }
                    return of(null);
                })
            )
            .subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    confirmSaveAndValidateAll() {
        if (this.planModelService.modelIsDirty() || this.spatialUtilService.hasEdits()) {
            this.confirmationModalService
                .showModal({
                    title: 'Save plan?',
                    confirmationText: 'The plan must be saved before rules can be run.<br>Do you want to save the plan?',
                    iconType: 'info'
                })
                .subscribe((action: string) => {
                    if (action === 'yes') {
                        this.validateAll();
                    }
                });
        } else {
            this.validateAll();
        }
    }
}
